/** Cookie CRUD, click ID capture, external ID, fbp/fbc helpers. */

import {
  CLICK_ID_PARAMS,
  COOKIE_MAX_AGE_CLICK_ID_DAYS,
  COOKIE_MAX_AGE_EXTERNAL_ID_DAYS,
  COOKIE_EXTERNAL_ID,
  EVENT_ID_PREFIX,
  STORAGE_KEY_BOOT,
} from "./constants.js";
import { state } from "./state.js";
import { isMarketingAllowed } from "./consent.js";
import { pickCookieDomain } from "./cookie-domain.js";
import { parseGclCookie, parseUetCookie, isUsableFbc } from "./vendor-click-ids.js";
import { readArrivingAnchor } from "./cross-domain.js";

// --- Cookie CRUD ---

export function getCookie(name: string): string | null {
  const value = "; " + document.cookie;
  const parts = value.split("; " + name + "=");
  // The FIRST match, deliberately, and the server does the same since 13 August
  // 2026 (see parseTrackingCookies). When two cookies share a name the browser
  // sends the most specific one first, and the two sides used to disagree about
  // which one counted: the script read the first, the config route the last. On
  // a browser holding both a host-only and a domain-scoped anchor that means
  // the pixel and the server-side CAPI carry different identities for the same
  // event, which is not a missed match but a failed deduplication.
  if (parts.length >= 2) return parts[1].split(";")[0];
  return null;
}

/** The name of the throwaway cookie used to ask the browser about a domain. */
const COOKIE_DOMAIN_PROBE = "_tst_d";

/**
 * Does this browser accept a cookie scoped to this domain? Set one, look, and
 * clean up after ourselves either way.
 */
function acceptsCookieDomain(candidate: string): boolean {
  document.cookie = COOKIE_DOMAIN_PROBE + "=1; domain=" + candidate + "; path=/; SameSite=Lax";
  const accepted = document.cookie.indexOf(COOKIE_DOMAIN_PROBE + "=1") >= 0;
  if (accepted) {
    document.cookie =
      COOKIE_DOMAIN_PROBE + "=; domain=" + candidate + "; path=/; max-age=0; SameSite=Lax";
  }
  return accepted;
}

/**
 * The broadest domain this browser will accept a cookie on, asked once.
 *
 * The decision itself lives in `cookie-domain.ts`, where it can be tested
 * without a browser; this is the part that needs a real document.
 */
let cachedCookieDomain: string | null | undefined;

export function cookieDomain(): string | null {
  if (cachedCookieDomain !== undefined) return cachedCookieDomain;
  cachedCookieDomain = null;
  try {
    cachedCookieDomain = pickCookieDomain(window.location.hostname, acceptsCookieDomain);
  } catch {
    /* no document.cookie access; host-only is the only option left */
  }
  return cachedCookieDomain;
}

/**
 * Nothing lands on the visitor's device before the config has come back.
 *
 * `captureClickIds()` and `getFbc()` run on load, so they used to write their
 * cookies before the answer that says whether this site gates on consent at all
 * was in. The gate below reads `state.config`, which is still null at that
 * moment, so it let every one of those writes through. A site with a banner
 * therefore stored a click id for a visitor who had not been asked yet, and the
 * setting that was supposed to prevent exactly that could not: it was consulted
 * a beat too late, and no error was raised because nothing failed.
 *
 * So writes queue until `unlockCookieWrites()`, which the config route calls in
 * both its outcomes. This is also the precondition for computing a fingerprint,
 * where doing the work first and discarding it afterwards is itself the
 * violation, not a near miss.
 *
 * If the config never settles the queue is never flushed and nothing is stored,
 * which is the right way round: with no answer from the server there is no
 * consent state to act on, and events are being buffered rather than sent
 * anyway.
 */
let cookieWritesUnlocked = false;

/** The cookies the config route sets over HTTP; see `utils/tracking-cookies.ts`. */
const SERVER_MANAGED_COOKIES = ["_fbp", "_fbc", COOKIE_EXTERNAL_ID];

interface PendingCookieWrite {
  readonly name: string;
  readonly value: string;
  readonly maxAgeDays: number;
}

/** Ample for the click ids on one landing hit; a bound, not a budget. */
const PENDING_COOKIE_WRITES_MAX = 25;

const pendingCookieWrites: PendingCookieWrite[] = [];

export function unlockCookieWrites(): void {
  cookieWritesUnlocked = true;
  const queued = pendingCookieWrites.splice(0, pendingCookieWrites.length);
  for (const write of queued) {
    setCookie(write.name, write.value, write.maxAgeDays);
  }
}

export function setCookie(name: string, value: string, maxAgeDays: number): void {
  if (!cookieWritesUnlocked) {
    if (pendingCookieWrites.length < PENDING_COOKIE_WRITES_MAX) {
      pendingCookieWrites.push({ name, value, maxAgeDays });
    }
    return;
  }

  // Server-side cookies handle persistence via HTTP Set-Cookie headers, but
  // only for the three the server actually sets.
  //
  // This used to skip EVERY write in first-party mode, and it got away with it
  // by accident: the click ids were written eagerly on load, while
  // `state.serverCookies` was still false, so they slipped through before the
  // flag could stop them. Now that writes wait for the config, that accident is
  // gone, and a blanket skip would drop `_tst_gclid` and friends on exactly the
  // customers who have the strongest setup. Nobody sets those server-side, so
  // the landing page's click id would be gone by the next page.
  if (state.serverCookies && SERVER_MANAGED_COOKIES.indexOf(name) >= 0) return;

  if (state.config?.consent?.noCookieOnDenied) {
    if (!isMarketingAllowed() && name !== COOKIE_EXTERNAL_ID) {
      return;
    }
  }
  const domain = cookieDomain();
  document.cookie =
    name +
    "=" +
    value +
    "; path=/; max-age=" +
    maxAgeDays * 24 * 60 * 60 +
    "; SameSite=Lax" +
    (domain ? "; domain=" + domain : "");
}

/**
 * Fold a host-only anchor into the domain-scoped one, keeping the older value.
 *
 * Only runs on a browser that already visited before the `Domain` fix landed.
 * Without it that visitor carries two `_tst_eid` cookies: the old host-only one
 * and a fresh domain-scoped one, which is exactly the duplicate that made the
 * script and the server disagree. Adopting the existing value rather than
 * minting a new one is what keeps their history: a new anchor is not a bug, it
 * is a new person.
 */
function collapseDuplicateAnchors(): void {
  try {
    const matches = document.cookie
      .split("; ")
      .filter((entry) => entry.indexOf(COOKIE_EXTERNAL_ID + "=") === 0);
    if (matches.length < 2) return;

    const keep = matches[0].substring(COOKIE_EXTERNAL_ID.length + 1);
    // Delete the host-only variant. Same name, no domain: the browser only
    // removes the cookie whose scope matches, so the domain-scoped one survives.
    document.cookie = COOKIE_EXTERNAL_ID + "=; path=/; max-age=0; SameSite=Lax";
    const domain = cookieDomain();
    if (domain) {
      document.cookie =
        COOKIE_EXTERNAL_ID +
        "=" +
        keep +
        "; path=/; max-age=" +
        COOKIE_MAX_AGE_EXTERNAL_ID_DAYS * 24 * 60 * 60 +
        "; SameSite=Lax; domain=" +
        domain;
    }
  } catch {
    /* nothing to collapse if the jar is unreadable */
  }
}

/**
 * A value this browser recognises itself by across its own tabs.
 *
 * Only used to stop a fresh visitor who opens several tabs at once from being
 * handed a different anchor per tab: the config route mints one anchor per boot
 * id for the length of the race and no longer (see `anchor-mint-cache.ts`, which
 * explains why the server cannot key that on the address instead).
 *
 * Deliberately NOT a fallback identity. It is never sent with an event, never
 * stored next to a person, and the anchor is never derived from it, because a
 * value in localStorage that survives a cookie wipe and restores the old
 * identity is a respawner, whatever it is called in the code.
 */
export function getBootId(): string | null {
  try {
    const existing = localStorage.getItem(STORAGE_KEY_BOOT);
    if (existing) return existing;
    const fresh = Math.random().toString(36).substring(2, 14) + Math.random().toString(36).substring(2, 14);
    localStorage.setItem(STORAGE_KEY_BOOT, fresh);
    return fresh;
  } catch {
    // Private mode, storage disabled, or a full quota. Then the race stands,
    // which is what happened on every visit until today.
    return null;
  }
}

/**
 * Take over the anchor a declared domain sent along in the link.
 *
 * Only when this browser has none of its own: overwriting an existing anchor
 * would let any link decide who a returning visitor is, and that is a door
 * nobody should be able to open from the outside. A fresh browser landing on
 * the second domain of a funnel is exactly the case this exists for.
 */
export function adoptArrivingAnchor(): void {
  const arriving = readArrivingAnchor();
  if (!arriving) return;
  if (getCookie(COOKIE_EXTERNAL_ID)) return;
  setCookie(COOKIE_EXTERNAL_ID, arriving, COOKIE_MAX_AGE_EXTERNAL_ID_DAYS);
}

// --- Event ID ---

export function generateEventId(): string {
  return EVENT_ID_PREFIX + Date.now() + "_" + Math.random().toString(36).substring(2, 11);
}

// --- URL parameter helper ---

function getUrlParam(name: string): string | null {
  return new URLSearchParams(window.location.search).get(name);
}

// --- Multi-platform Click ID Capture ---

/**
 * The click ids that were on the address bar when this page loaded.
 *
 * Separate from `getClickIds()` on purpose, and the difference decides who gets
 * credit for a visit. `getClickIds()` reads COOKIES, which live for ninety days,
 * so a visitor who clicked an ad in May still arrives carrying a gclid in
 * August. Handing that to the source resolver means every later visit, from a
 * blog or from search or from nowhere at all, is booked on that ad, and the
 * touch chain never records the real one because the channel never changes.
 *
 * The cookie value stays exactly as useful as it was for the ad platforms: they
 * match on it, and matching a ninety-day-old click is the whole point. It is
 * only the claim "this visit came from there" that needs the fresh URL.
 *
 * Read once and remembered, because a site's own tag can rewrite the address
 * bar (Google's does) and later events would then see a URL the visitor never
 * landed on. Everything on one page load belongs to the arrival that started
 * it, including the SPA routes after it.
 */
let arrivalClickIds: Record<string, string> | null = null;

export function getArrivalClickIds(): Record<string, string> {
  if (arrivalClickIds) return arrivalClickIds;
  const ids: Record<string, string> = {};
  for (const param of Object.keys(CLICK_ID_PARAMS)) {
    const value = getUrlParam(param);
    if (value) ids[param] = value;
  }
  arrivalClickIds = ids;
  return ids;
}

export function captureClickIds(): void {
  // Pin the arrival set first: this runs at init, the earliest moment we can
  // still trust the address bar.
  getArrivalClickIds();
  for (const [param, config] of Object.entries(CLICK_ID_PARAMS)) {
    const value = getUrlParam(param);
    if (value) {
      const stored = config.format ? config.format(value) : value;
      setCookie(config.cookie, stored, COOKIE_MAX_AGE_CLICK_ID_DAYS);
    }
  }
}

export function getClickIds(): Record<string, string> {
  const ids: Record<string, string> = {};
  for (const [param, config] of Object.entries(CLICK_ID_PARAMS)) {
    const value = getCookie(config.cookie);
    if (value) ids[param] = value;
  }
  // Google's own tag has been storing the same gclid in _gcl_aw for ninety
  // days all along, and we only ever read the URL. That misses every visitor
  // whose ad click predates our install, who landed on a page we were not on
  // yet, or whose landing hit we simply were not there for. _gcl_dc is the
  // display click, _gcl_gb the gbraid.
  if (!ids.gclid) {
    const gcl = parseGclCookie(getCookie("_gcl_aw"));
    if (gcl) ids.gclid = gcl;
  }
  if (!ids.gclid) {
    const dc = parseGclCookie(getCookie("_gcl_dc"));
    if (dc) ids.gclid = dc;
  }
  if (!ids.gbraid) {
    const gb = parseGclCookie(getCookie("_gcl_gb"));
    if (gb) ids.gbraid = gb;
  }
  // Microsoft's UET tag keeps the msclkid in its own cookie, same idea.
  if (!ids.msclkid) {
    const uet = parseUetCookie(getCookie("_uetmsclkid"));
    if (uet) ids.msclkid = uet;
  }
  // An _fbc older than the window it was written with cannot be attributed by
  // anyone. Meta drops it without a word, which reads as a delivered
  // conversion that matched nothing.
  if (ids.fbclid && !isUsableFbc(getCookie("_tst_fbc"), COOKIE_MAX_AGE_CLICK_ID_DAYS, Date.now())) {
    delete ids.fbclid;
  }
  // LinkedIn first-party click id: the Insight Tag persists li_fat_id as its
  // own cookie (30 days from the last ad click). Fallback for visitors whose
  // first ad click predates our script or whose _tst_li cookie is gone.
  if (!ids.li_fat_id) {
    const liFat = getCookie("li_fat_id");
    if (liFat) ids.li_fat_id = liFat;
  }
  // Pinterest click id: Pinterest's own tag persists the same epik value in
  // _epik; fallback for visitors whose first click predates our script
  if (!ids.epik) {
    const epik = getCookie("_epik");
    if (epik) ids.epik = epik;
  }
  // TikTok browser id: set by the TikTok pixel as a first-party cookie (not a
  // URL param), passed server-side as user.ttp for better match rates
  const ttp = getCookie("_ttp");
  if (ttp) ids.ttp = ttp;
  // Snapchat first-party pixel cookie (sc_cookie1): set by the Snap Pixel when
  // present, passed server-side to lift match rates (mirrors TikTok's _ttp)
  const scid = getCookie("_scid");
  if (scid) ids.scid = scid;
  // Reddit Pixel first-party cookie: enables Reddit's session-based dedup and
  // better matching, passed server-side as user.uuid (mirrors _scid/_ttp)
  const rdtUuid = getCookie("_rdt_uuid");
  if (rdtUuid) ids.rdt_uuid = rdtUuid;
  // Reddit's own click-id cookie, which its tag writes for 90 days under the
  // name `_rdt_cid`. Same fallback shape as _gcl_aw and _epik above, and it
  // covers the two cases our own `_tst_rdtcid` cannot: a visitor whose ad click
  // predates our install, and a site already running Reddit's tag where the
  // click landed on a page we were not on yet. Reddit reads this cookie itself
  // in its official server-side GTM template, so the name and lifetime are its
  // own, not ours.
  if (!ids.rdt_cid) {
    const rdtCid = getCookie("_rdt_cid");
    if (rdtCid) ids.rdt_cid = rdtCid;
  }
  // OpenAI Ads pixel cookies. __oppref carries the ad-click attribution
  // reference OpenAI's own tag parsed from the landing URL (the URL param
  // name is undocumented; only their pixel knows it, so the cookie is the
  // only capture point). __obref is the browser reference for the CAPI user
  // object. Two near-identical names, two different fields; obref is
  // consent-bound by OpenAI's docs ("if the user revokes consent, stop
  // sending it"), so it is only read while marketing consent allows.
  const oppref = getCookie("__oppref");
  if (oppref) ids.oppref = oppref;
  if (isMarketingAllowed()) {
    const obref = getCookie("__obref");
    if (obref) ids.obref = obref;
  }
  // GA client/session ids: let server-side GA4 events join the real browser
  // session (without them GA reports source "(not set)")
  const gaCid = parseGaClientId();
  if (gaCid) ids.ga_cid = gaCid;
  const gaSid = parseGaSessionId();
  if (gaSid) ids.ga_sid = gaSid;
  return ids;
}

/**
 * Parse the GA client id from the _ga cookie. Legacy format
 * "GA1.1.<random>.<timestamp>" (client_id = last two segments); parse
 * defensively because Google is rolling out new formats (GS2-style).
 */
function parseGaClientId(): string | null {
  const ga = getCookie("_ga");
  if (!ga) return null;
  const parts = ga.split(".");
  if (parts.length >= 4) {
    const random = parts[parts.length - 2];
    const timestamp = parts[parts.length - 1];
    if (/^\d+$/.test(random) && /^\d+$/.test(timestamp)) {
      return random + "." + timestamp;
    }
  }
  return null;
}

/**
 * Parse the GA4 session id from a _ga_<container> cookie.
 * GS1 format: "GS1.1.<session_id>...."; GS2 format: "GS2.1.s<session_id>$...".
 */
function parseGaSessionId(): string | null {
  const cookies = document.cookie.split("; ");
  for (const entry of cookies) {
    const eq = entry.indexOf("=");
    if (eq < 0) continue;
    const name = entry.substring(0, eq);
    if (!name.startsWith("_ga_")) continue;
    const value = entry.substring(eq + 1);
    // GS2: key-value parts separated by $, session id prefixed with s
    const gs2Match = value.match(/\bs(\d{8,})/);
    if (value.startsWith("GS2.") && gs2Match) return gs2Match[1];
    // GS1: third dot-separated segment is the session id (unix seconds)
    const parts = value.split(".");
    if (parts.length >= 3 && /^\d{8,}$/.test(parts[2])) return parts[2];
  }
  return null;
}

// --- Facebook fbc/fbp ---

export function getFbc(): string | null {
  const fbclid = getUrlParam("fbclid");
  if (fbclid) {
    const fbc = "fb.1." + Date.now() + "." + fbclid;
    setCookie("_fbc", fbc, COOKIE_MAX_AGE_CLICK_ID_DAYS);
    // Update server state so it's included in event payloads
    if (state.serverCookies) state.serverFbc = fbc;
    return fbc;
  }
  if (state.serverCookies && state.serverFbc) return state.serverFbc;
  return getCookie("_fbc") || getCookie("_tst_fbc");
}

/**
 * Meta's cookie, read and never written.
 *
 * We used to mint one in Meta's format whenever `fbevents.js` had not set one
 * yet. It looked like a win and it was not:
 *
 * Measured on production, 12 August 2026: on websites with NO Meta destination
 * at all, 928 of 928 identity rows carried an `_fbp`. There is no pixel on
 * those pages, so every one of those values was ours. They match nothing at
 * Meta, they made the coverage figure on the health screen read almost 100%,
 * and they became this visitor's stored `fbp` in `visitor_identities`, where
 * they then acted as an anchor for a browser Meta has never heard of. On sites
 * that DO have Meta, 1620 of 1628 rows had one, which is more complete than
 * Meta's own cookie ever is; we were filling that gap too.
 *
 * Worse than useless, in fact: if our value lands first and `fbevents.js`
 * writes its own a moment later, the browser holds two `_fbp` values, the pixel
 * sends one and our CAPI copy sends the other, and the deduplication between
 * them cannot land.
 *
 * So: return what Meta set, or nothing. There is no polling loop, because
 * `getFbp` is read again for every event; a pixel that initialises a second
 * later is simply picked up by the next one.
 *
 * EXPECT THE COVERAGE NUMBER TO DROP. `EventRepo` counts `'fbp' = ANY(
 * user_data_keys)` as a match-quality figure, and it will fall sharply with a
 * kink on the deploy date. That is the fabricated share leaving the number, not
 * a regression.
 */
export function getFbp(): string | null {
  // Server-side cookies: use the value provided by the server
  if (state.serverCookies && state.serverFbp) return state.serverFbp;

  // Check for duplicate _fbp cookies (hostname-scoped vs root-domain).
  // Previous script versions created _fbp on the hostname, conflicting
  // with the FB pixel SDK's root-domain cookie. Delete hostname-scoped
  // duplicate so our value matches the browser pixel's.
  const allCookies = document.cookie.split("; ");
  const fbpCount = allCookies.filter((cookie) => cookie.startsWith("_fbp=")).length;
  if (fbpCount > 1) {
    document.cookie = "_fbp=; path=/; max-age=0; SameSite=Lax";
  }

  return getCookie("_fbp");
}

// --- External ID ---

export function getExternalId(): string {
  if (state.serverCookies && state.serverExternalId) return state.serverExternalId;

  collapseDuplicateAnchors();
  let id = getCookie(COOKIE_EXTERNAL_ID);
  if (!id) {
    id = EVENT_ID_PREFIX + Date.now() + "_" + Math.random().toString(36).substring(2, 14);
    setCookie(COOKIE_EXTERNAL_ID, id, COOKIE_MAX_AGE_EXTERNAL_ID_DAYS);
  }
  return id;
}
